import { Component } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import {
  AssetState,
  ConnectPaymentMethod,
  DeleteConnectedPaymentMethod,
  MSG_DELETE_CONNECTED_PAYMENT_METHOD_WARNING,
  SubscriptionBillingState,
  SubscriptionPaymentState
} from '@platform-frontend/checkout';
import {
  DeletePaymentMethod,
  isReusablePaymentMethod,
  isReusablePaymentMethodIdentifier,
  PaymentMethod,
  PaymentMethodIdentifier,
  PaymentMethodInfo,
  WalletPaymentMethodType
} from '@platform-frontend/marketplace';
import { InclusiveActionFilter } from '@platform-frontend/ui-tools';
import { ClearNotifications, filterTrue, InvalidStateError, ShowWarningNotification, tapNextOrError } from '@platform-frontend/util';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { PaymentIdWithCallback } from '../../commercial-common/payment-id-with-callback';

@UntilDestroy()
@Component({
  selector: 'yat-payment-method-selection',
  templateUrl: './payment-method-selection.component.html',
  styleUrls: ['./payment-method-selection.component.scss']
})
export class PaymentMethodSelectionComponent {
  constructor(private store: Store) {}

  @Select(SubscriptionPaymentState.paymentMethods)
  paymentMethods$: Observable<(PaymentMethodInfo | PaymentMethod)[]>;

  @Select(SubscriptionBillingState.connectedCountry)
  connectedCountry$: Observable<string | null>;

  @Select(SubscriptionPaymentState.connectedPaymentMethod)
  connectedPaymentMethod$: Observable<PaymentMethod | null>;

  @Select(SubscriptionPaymentState.canConnectedPaymentMethodBeReplaced)
  canConnectedPaymentMethodBeReplaced$: Observable<boolean>;

  @Select(AssetState.contractId)
  managedContractId$: Observable<string | null>;

  connectActionFilter = new InclusiveActionFilter(ConnectPaymentMethod);

  saveConnectedPaymentMethod({ identifier, completeCallback }: PaymentIdWithCallback): void {
    if (!isReusablePaymentMethodIdentifier(identifier)) {
      throw new InvalidStateError('Wallet payment data has to be confirmed before connecting');
    }
    this.store
      .dispatch(new ConnectPaymentMethod(identifier))
      .pipe(
        tapNextOrError(() => completeCallback()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  prepareDeletePaymentMethod(identifier: PaymentMethodIdentifier): void {
    this.isConnected(identifier)
      .pipe(
        filterTrue(),
        switchMap(() => this.store.dispatch([new ClearNotifications(), new ShowWarningNotification(MSG_DELETE_CONNECTED_PAYMENT_METHOD_WARNING)])),
        untilDestroyed(this)
      )
      .subscribe();
  }

  deletePaymentMethod({ identifier, completeCallback }: PaymentIdWithCallback): void {
    this.isConnected(identifier)
      .pipe(
        switchMap(deleteConnectedMethod =>
          deleteConnectedMethod ? this.store.dispatch(new DeleteConnectedPaymentMethod()) : this.store.dispatch(new DeletePaymentMethod(identifier))
        ),
        tapNextOrError(() => completeCallback()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private isConnected(identifier: PaymentMethodIdentifier): Observable<boolean> {
    return this.connectedPaymentMethod$.pipe(
      take(1),
      map(connectedMethod =>
        isReusablePaymentMethodIdentifier(identifier)
          ? connectedMethod !== null && isReusablePaymentMethod(connectedMethod) && identifier.id === connectedMethod.id
          : identifier.type === connectedMethod?.type
      )
    );
  }

  toWalletPaymentMethodType(activeWalletData: WalletPaymentMethodType): WalletPaymentMethodType {
    return activeWalletData;
  }
}
